import cn from 'classnames';
import getConfig from 'next/config';
import type { FC, ReactNode } from 'react';
import React from 'react';

import { getAntifraudSdkScript } from '@sravni/antifraud-sdk';
import { Text } from '@sravni/react-design-system/lib/Typography';
import { useIsMobile } from '@sravni/react-utils';

import type { FILTERS_NAMES } from '@src/constants/filters';
import { useIsPromoActive } from '@src/reducers/promo';
import { useSelectUser } from '@src/reducers/user';
import { ADengiBanner } from 'entities/Banner/ui/ADengiBanner/ADengiBanner';
import { ADENGI_PROMO_DOCUMENT_LINK } from 'entities/Banner/ui/ADengiBanner/ADengiBanner.config';
import { FullDealEntryPointBanner } from 'entities/FullDeal';
import { TO_FULL_DEAL_BUTTON_DEFAULT_TEXT } from 'entities/FullDeal/config';
import { useMicrocreditDeals } from 'entities/FullDeal/lib';
import { FullDealEntryPointDisclaimer } from 'entities/FullDeal/ui/FullDealEntryPointDisclaimer/FullDealEntryPointDisclaimer';
import { InlineCalculations } from 'entities/FullDeal/ui/InlineCalculations/InlineCalculations';
import { getPromoVariant } from 'entities/Promo/lib/getPromoVariant';
import type { FullDealOfferActionsProps } from 'features/FullDeal';
import { FullDealEntryPointForm, FullDealOfferActions } from 'features/FullDeal';
import { openPersonalProfile } from 'features/FullDeal/lib';
import { PromoDealOffersActions } from 'features/Promo';
import { useScriptLoader } from 'shared/hooks/useScriptLoader';

import { useOnChangeCalculator } from '../../lib';
import type { RenderActionsProps } from '../../types';

import styles from './FullDealEntryPoint.module.scss';

export interface FullDealEntryPointProps {
  isHiddenBorder?: boolean;
  heading?: string;

  isLanding?: boolean;
  highlightOffer?: boolean;

  onChange?: (key: FILTERS_NAMES, value: number) => void;
  renderActions?: (props: RenderActionsProps) => ReactNode;
}

export const FullDealEntryPoint: FC<FullDealEntryPointProps> = ({
  heading,
  highlightOffer = true,
  onChange,
  renderActions,
  isLanding,
  isHiddenBorder,
}) => {
  const isMobile = useIsMobile();
  const user = useSelectUser();

  const { hasForm, hasOffer, isOfferWaiting, toFullDealButtonText, hasIssuedMicrocreditDeals } = useMicrocreditDeals();

  const { amount, term, handleChange } = useOnChangeCalculator({
    onChange,
  });

  const isPromoActive = useIsPromoActive();

  // @todo: показываем калькулятор если нет активного офера и шаг не ожидание оффера
  const isShowCalculator = !hasOffer && !isOfferWaiting;
  const hasOfferOrForm = hasOffer || hasForm || isOfferWaiting;
  const _toFullDealButtonText = toFullDealButtonText || TO_FULL_DEAL_BUTTON_DEFAULT_TEXT;

  const highlightActiveOffer = hasOfferOrForm && highlightOffer;

  const FullDealOfferActionsVariant = getPromoVariant<FullDealOfferActionsProps>(
    isPromoActive && isMobile && !hasOfferOrForm,
    PromoDealOffersActions,
    FullDealOfferActions,
  );

  useScriptLoader(
    user?.sub ? getAntifraudSdkScript({ environment: getConfig().publicRuntimeConfig.environment }) : null,
  );

  return (
    <div
      className={cn(styles.relativeContainer, {
        [styles.asLandingContainer]: isLanding,
        [styles.relativeHighlighter]: highlightActiveOffer,
      })}
    >
      {highlightActiveOffer && (
        <Text strong className={cn(styles.withOfferOrFormText)}>
          Продолжите оформление займа
        </Text>
      )}

      <div
        className={cn(styles.container, {
          [styles.withOfferOrForm]: highlightActiveOffer,
          [styles.hiddenBorder]: isHiddenBorder,
        })}
      >
        <div className={cn(styles.content, { [styles.withOfferOrFormContent]: highlightActiveOffer })}>
          <FullDealEntryPointBanner
            isMobile={isMobile}
            mobileBanner={<ADengiBanner isShowDisplayPixel={false} />}
            isLanding={isLanding}
            heading="Подберем займ"
            hasOffer={hasOffer}
          />
          {isShowCalculator && (
            <FullDealEntryPointForm amount={amount} term={term} onChange={handleChange} isCustomTheme />
          )}

          <InlineCalculations isMobile={isMobile} amount={amount} term={term as number}>
            {!renderActions && (
              <FullDealOfferActionsVariant
                amount={amount}
                term={term}
                className={styles.redButton}
                toFullDealButtonColor="red"
                hasIssuedMicrocreditDeals={hasIssuedMicrocreditDeals}
                toFullDealButtonText={_toFullDealButtonText}
                onMyLoansClick={openPersonalProfile}
                seoHeading={heading}
              />
            )}

            {renderActions?.({
              amount,
              term,
              hasOffer,
              toFullDealButtonText,
            })}
          </InlineCalculations>
          <FullDealEntryPointDisclaimer link={ADENGI_PROMO_DOCUMENT_LINK} className="desktop:hidden" />
        </div>

        <div className={cn('mobile:hidden', styles.serviceOneContainer)}>
          <ADengiBanner isShowDisplayPixel />
        </div>
      </div>
      <FullDealEntryPointDisclaimer
        link={ADENGI_PROMO_DOCUMENT_LINK}
        className={cn('mobile:hidden', { [styles.disclaimerLanding]: isLanding })}
      />
    </div>
  );
};
